/**
 * react-router v6/v7 compatibility shim over react-router-dom v5 (TRAV23 activities merge).
 *
 * The activities front was written against react-router-dom v7 (`useNavigate`,
 * `<Navigate>`, `useSearchParams`). RSI is pinned to v5.3.4 and cannot be upgraded:
 * 262 modules import from `react-router-dom` and 146 of them are class components
 * wrapped in `withRouter` / typed with `RouteComponentProps`.
 *
 * Rather than rewrite every ported activities module, they import these helpers and
 * keep their original call shapes. Route *declarations* are not shimmed — those are
 * rewritten to v5 `<Switch>` / `<Route component=>` in the activities route tree.
 *
 * Do not use this shim in existing RSI code; it exists only for ported modules.
 */

import React, { useCallback, useMemo } from 'react';

import { Redirect, useHistory, useLocation } from 'react-router-dom';

import { history } from './store';

export interface INavigateOptions {
  replace?: boolean;
  state?: unknown;
}

/** v6/v7 `NavigateFunction`: a path plus options, or a history delta. */
export interface INavigateFunction {
  (to: string, options?: INavigateOptions): void;
  (delta: number): void;
}

/**
 * v6/v7 `useNavigate` implemented on v5's `useHistory`.
 *
 * `relative` is deliberately unsupported: v5 has no relative-resolution equivalent,
 * and silently treating a relative path as absolute would route to the wrong page.
 */
export const useNavigate = (): INavigateFunction => {
  const history = useHistory();

  return useCallback(
    (to: string | number, options?: INavigateOptions) => {
      if (typeof to === 'number') {
        history.go(to);
        return;
      }

      if (options?.replace) {
        history.replace(to, options?.state);
        return;
      }

      history.push(to, options?.state);
    },
    [history],
  ) as INavigateFunction;
};

export interface INavigateProps {
  to: string;
  replace?: boolean;
  state?: unknown;
}

/** v6/v7 `<Navigate>` implemented on v5's `<Redirect>`. */
export const Navigate: React.FC<INavigateProps> = ({ to, replace, state }) => (
  <Redirect to={state ? { pathname: to, state } : to} push={!replace} />
);

/**
 * Read-only half of v6/v7 `useSearchParams`.
 *
 * The setter is intentionally omitted: v6 merges the current location on set, and a
 * partial imitation would drop state in ways that are hard to spot at review time.
 * Ported code that needs to write query params uses `history.push` with an explicit
 * search string instead.
 */
export const useSearchParams = (): [URLSearchParams] => {
  const location = useLocation();

  return useMemo(() => [new URLSearchParams(location.search)], [location.search]);
};

/**
 * Navigate from non-component code (thunks, axios interceptors, services).
 *
 * The activities front solved this by stashing the `useNavigate` function in a module
 * global (`setNavigator`) from its App component. RSI already owns a module-level
 * `history` (see `./store`), so ported code calls this instead and there is nothing
 * to register at startup.
 *
 * `./store` is already part of a `@share/utils` <-> `@share/store/slices` import cycle;
 * `history` is only read when this function runs, never at module evaluation, so the
 * cycle resolves safely.
 */
export const navigateTo = (path: string, options?: INavigateOptions): void => {
  if (options?.replace) {
    history.replace(path, options?.state);
    return;
  }

  history.push(path, options?.state);
};
